// Pure helpers for the Fact Check sidebar: word diff, reason labels, grouping
// and ordering. No React here so tests/factCheckSidebarUtils.test.cjs can run
// them under plain node.

export interface SidebarFinding {
  id: number;
  category: string;
  original_text: string;
  reasoning: string;
  decision?: string | null;
  escalation_reason?: string | null;
  depends_on_finding_ids?: Array<number | string> | null;
  caused_by?: number | string | null;
  claim_id?: string | null;
  locator?: { start_char?: number | null } | null;
}

export type DiffPart = { type: "same" | "del" | "ins"; text: string };

// Beyond this many token pairs the diff is not worth computing: show the
// original as removed and the replacement as added.
const MAX_DIFF_CELLS = 200000;

const tokenize = (text: string): string[] => text.match(/\s+|[^\s]+/g) || [];

// Below this share of the original's characters kept, the replacement is a
// rewrite: marking word by word only produces alternating fragments.
const MIN_KEPT_SHARE = 0.4;
// Unchanged words this short between two changes join the change, so a
// rewritten phrase reads as one removal and one addition.
const MAX_JOINED_WORD_LENGTH = 3;

const visibleLength = (text: string) => text.replace(/\s+/g, "").length;

/**
 * Word-level diff of `before` → `after` for the editor: removals come before
 * additions, short connecting words between changes join the change, and a
 * near-complete rewrite comes back as the whole old text then the new text.
 */
export const diffWords = (before: string, after: string): DiffPart[] => {
  const a = tokenize(before);
  const b = tokenize(after);
  const rewrite = (): DiffPart[] =>
    [
      { type: "del" as const, text: before },
      { type: "ins" as const, text: after },
    ].filter((p) => p.text.length > 0);

  if (a.length * b.length > MAX_DIFF_CELLS) return rewrite();

  // lcs[i][j] = length of the longest common subsequence of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  // Runs of unchanged tokens alternating with changes ({del, ins} pairs).
  type Run = { same: string } | { del: string; ins: string };
  const runs: Run[] = [];
  const addSame = (text: string) => {
    const last = runs[runs.length - 1];
    if (last && "same" in last) last.same += text;
    else runs.push({ same: text });
  };
  const addChange = (del: string, ins: string) => {
    const last = runs[runs.length - 1];
    if (last && "del" in last) {
      last.del += del;
      last.ins += ins;
    } else runs.push({ del, ins });
  };
  let i = 0;
  let j = 0;
  let kept = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      kept += visibleLength(a[i]);
      addSame(a[i]);
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      addChange(a[i++], "");
    } else {
      addChange("", b[j++]);
    }
  }
  while (i < a.length) addChange(a[i++], "");
  while (j < b.length) addChange("", b[j++]);

  const total = visibleLength(before);
  if (total > 0 && kept / total < MIN_KEPT_SHARE) return rewrite();

  const joinable = (text: string) => {
    const words = text.trim().split(/\s+/).filter(Boolean);
    return words.length === 0 || (words.length === 1 && words[0].length <= MAX_JOINED_WORD_LENGTH);
  };
  const merged: Run[] = [];
  runs.forEach((run) => {
    const between = merged[merged.length - 1];
    const prior = merged[merged.length - 2];
    if ("del" in run && between && "same" in between && prior && "del" in prior && joinable(between.same)) {
      merged.pop();
      prior.del += between.same + run.del;
      prior.ins += between.same + run.ins;
    } else {
      merged.push({ ...run });
    }
  });

  const parts: DiffPart[] = [];
  merged.forEach((run) => {
    if ("same" in run) parts.push({ type: "same", text: run.same });
    else {
      if (run.del) parts.push({ type: "del", text: run.del });
      if (run.ins) parts.push({ type: "ins", text: run.ins });
    }
  });
  return parts;
};

const DOWNSTREAM_PREFIX = /^\s*Downstream of finding #(\d+)/i;

/** The finding this one follows from, if any. */
export const parentIdOf = (finding: SidebarFinding): number | null => {
  const candidates: Array<number | string | null | undefined> = [
    ...(Array.isArray(finding.depends_on_finding_ids) ? finding.depends_on_finding_ids : []),
    finding.caused_by,
    (finding.reasoning || "").match(DOWNSTREAM_PREFIX)?.[1],
  ];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const id = Number(candidate);
    if (Number.isFinite(id) && id !== finding.id) return id;
  }
  return null;
};

export type ReasonTone = "escalate" | "confirmed" | "neutral";

const ESCALATION_LABELS: Record<string, string> = {
  missing_evidence: "No source found",
  scope_or_qualifier_gap: "Missing qualifier",
  source_conflict: "Sources disagree",
  unsafe_replacement: "Unsafe replacement",
  policy_guard: "Policy check",
};

export const categoryLabel = (category: string): string =>
  String(category || "")
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");

/** One short label saying why the finding needs the editor. */
export const reasonLabel = (
  finding: SidebarFinding,
  decision?: { decision?: string } | null
): { text: string; tone: ReasonTone } => {
  const escalation = finding.escalation_reason || "";
  if (ESCALATION_LABELS[escalation]) return { text: ESCALATION_LABELS[escalation], tone: "escalate" };
  const parent = parentIdOf(finding);
  if (parent !== null) return { text: `Follows from #${parent}`, tone: "neutral" };
  if (finding.decision === "apply" || decision?.decision === "accept") {
    return { text: "Correction confirmed", tone: "confirmed" };
  }
  return { text: categoryLabel(finding.category), tone: "neutral" };
};

export interface FindingGroup<T extends SidebarFinding> {
  main: T;
  related: T[];
}

const normalizeText = (text: string) =>
  String(text || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

/**
 * Groups related findings (a main finding with the findings that follow from
 * it, or findings on the same sentence or claim) and orders the groups as they
 * appear in the article. `positions` maps finding id to its place in the
 * editor document; without it the fact-check's own locator offsets are used.
 */
export const buildGroups = <T extends SidebarFinding>(
  findings: T[],
  positions?: Record<number, number> | null
): FindingGroup<T>[] => {
  const hasPositions = !!positions && Object.keys(positions).length > 0;
  const position = (f: T): number => {
    if (hasPositions) return positions![f.id] ?? Infinity;
    const start = f.locator?.start_char;
    return typeof start === "number" ? start : Infinity;
  };
  const byOrder = (x: T, y: T) => position(x) - position(y) || x.id - y.id;

  const ids = new Set(findings.map((f) => f.id));
  const root = new Map<number, number>(findings.map((f) => [f.id, f.id]));
  const find = (id: number): number => {
    let r = id;
    while (root.get(r) !== r) r = root.get(r)!;
    root.set(id, r);
    return r;
  };
  const union = (x: number, y: number) => {
    const rx = find(x);
    const ry = find(y);
    if (rx !== ry) root.set(ry, rx);
  };

  const firstByText = new Map<string, number>();
  const firstByClaim = new Map<string, number>();
  findings.forEach((f) => {
    const parent = parentIdOf(f);
    if (parent !== null && ids.has(parent)) union(parent, f.id);
    const text = normalizeText(f.original_text);
    if (text) {
      if (firstByText.has(text)) union(firstByText.get(text)!, f.id);
      else firstByText.set(text, f.id);
    }
    const claim = String(f.claim_id || "").trim();
    if (claim) {
      if (firstByClaim.has(claim)) union(firstByClaim.get(claim)!, f.id);
      else firstByClaim.set(claim, f.id);
    }
  });

  const members = new Map<number, T[]>();
  findings.forEach((f) => {
    const r = find(f.id);
    members.set(r, [...(members.get(r) || []), f]);
  });

  const groups: FindingGroup<T>[] = Array.from(members.values()).map((list) => {
    const sorted = [...list].sort(byOrder);
    const inGroup = new Set(list.map((f) => f.id));
    const main =
      sorted.find((f) => {
        const parent = parentIdOf(f);
        return parent === null || !inGroup.has(parent);
      }) || sorted[0];
    return { main, related: sorted.filter((f) => f !== main) };
  });
  return groups.sort((x, y) => byOrder(x.main, y.main));
};

/** "www.example.com/a/b/?q" → "example.com/a/b". Non-URLs come back as-is. */
export const shortUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/+$/, "");
    return parsed.hostname.replace(/^www\./, "") + path;
  } catch {
    return url;
  }
};

/** Source entries from the `;`-separated source string plus evidence URLs, deduped. */
export const collectSources = (source: string, extra: string[] = []): string[] => {
  const all = [
    ...String(source || "").split(/\s*;\s*/),
    ...extra,
  ]
    .map((s) => String(s || "").trim())
    .filter((s) => s.length > 0);
  return Array.from(new Set(all));
};
